import { FunctionComponent } from 'preact';
import { useTranslation } from 'react-i18next';
import { useFocus } from 'contexts/FocusContext';
import FocusManager from 'components/FocusManager';
import { usePTPConfigContext } from 'GlobalConfig.context';
import { CustomText } from 'components/CustomText';
import { Content } from '../Modal';
import { Tabs, TabList, TabPanel, TabPanels } from '../Tabs';
import {
  CustomHelperText,
  LoginModalHeader,
  LoginModalDialog,
  LoginModalBody,
  LoginSearchButton,
} from './LoginModal.styles';
import { LoginTab } from './components';
import { Input, Text } from '../LoginComponents';
import { useLoginModal } from './hooks';

interface LoginModalProps {
  isLoading?: boolean;
  hasError?: boolean;
  hasNoInfoUpdate?: boolean;
}

export const LoginModal: FunctionComponent<LoginModalProps> = ({
  isLoading,
  hasError = false,
  hasNoInfoUpdate = false,
}) => {
  const { t } = useTranslation();
  const { setFocusOnNextView } = useFocus();
  const { searchForm } = usePTPConfigContext();
  const { title, description, buttonText, shouldHideBorder } = searchForm;

  const {
    tabs,
    selectedTabIndex,
    shouldShowTabs,
    onChangeTab,

    formFields,
    onChangeForm,
    onSubmitForm,
    customError,
    customMessage,
  } = useLoginModal({ hasError, hasNoInfoUpdate });

  const handleSubmit = (event: Event) => {
    event.preventDefault();
    onSubmitForm(event);
    setFocusOnNextView();
  };

  return (
    <FocusManager shouldFocus={false}>
      <LoginModalDialog $hideBoxShadow={shouldHideBorder}>
        <CustomText
          as={LoginModalHeader}
          text={title || t('TRACK_YOUR_ORDER')}
        />
        <LoginModalBody>
          <form onSubmit={handleSubmit} noValidate>
            <Tabs
              activeTabIndex={selectedTabIndex}
              onSelectionChange={onChangeTab}
            >
              {shouldShowTabs && (
                <TabList>
                  {tabs.map(({ key, label }) => (
                    <LoginTab key={`tab-${key}`} id={key}>
                      {label}
                    </LoginTab>
                  ))}
                </TabList>
              )}

              <TabPanels>
                {tabs.map(({ key }) => (
                  <TabPanel key={`panel-${key}`}>
                    <Content>
                      {!shouldShowTabs && (
                        <CustomText
                          as={Text}
                          text={description || t('ENTER_SEARCH_ID')}
                        />
                      )}
                      {customMessage && (
                        <CustomText as={Text} text={customMessage} />
                      )}
                      {formFields.map((formField) => {
                        const {
                          name,
                          value,
                          label,
                          error,
                          tooltip,
                          placeholder,
                        } = formField;

                        return (
                          <Input
                            key={`input-${name}`}
                            name={name}
                            value={value}
                            label={label}
                            labelTooltip={tooltip}
                            error={t(error)}
                            onChange={onChangeForm}
                            placeholder={placeholder}
                          />
                        );
                      })}

                      <LoginSearchButton type="submit" disabled={isLoading}>
                        {buttonText || t('TRACK_NOW')}
                      </LoginSearchButton>

                      {customError && (
                        <CustomText
                          as={CustomHelperText}
                          text={customError}
                          isError
                          size="md"
                        />
                      )}
                    </Content>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </form>
        </LoginModalBody>
      </LoginModalDialog>
    </FocusManager>
  );
};
